import React from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { FormikProps } from "formik";
import { SimulatorTextField } from "../fields/SimulatorTextField";
import { FormValues } from "../types";
import {
  labelDemandaUsina,
  labelGeracao,
  ocultaCampo,
  SITUACAO_USINA_OPTIONS,
} from "../situacaoUsina";

const FormSwitch = (Form as any).Switch;

interface SelfProductionSectionProps {
  formik: FormikProps<FormValues>;
  buttonSelected: string;
  proposalPermission: boolean;
  changeSituacaoUsina: (e: any) => void;
  resetSimultaneidade: (e: any) => void;
  handleFileChange: (e: any, path: string) => void;
  handleItemChange: (
    index: number,
    event: { target: { name: string; value: string } }
  ) => void;
  addItem: () => void;
  removeItem: (index: number) => void;
}

export const SelfProductionSection = ({
  formik,
  buttonSelected,
  proposalPermission,
  changeSituacaoUsina,
  resetSimultaneidade,
  handleFileChange,
  handleItemChange,
  addItem,
  removeItem,
}: SelfProductionSectionProps) => (
  <>
    <div>
      <span className="simulacao-form-header">
        Dados para simular autoprodutor de energia
      </span>
      <hr />
    </div>

    <div className="formLine">
      <Form.Group
        controlId="situacaoUsina"
        style={{
          width: "100%",
          maxWidth: "375px",
          height: "80px",
        }}
      >
        <Form.Label className="simulacao-form-title">
          Situação da usina <span>*</span>
        </Form.Label>
        <Form.Control
          as="select"
          name="situacaoUsina"
          onBlur={formik.handleBlur}
          value={formik.values.situacaoUsina}
          onChange={changeSituacaoUsina}
          disabled={buttonSelected === "APEBESS"}
        >
          {SITUACAO_USINA_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {formik.values.situacaoUsina !== "USINA_CONSORCIO" && (
        <>
          <Form.Group
            controlId="local"
            style={{
              width: "100%",
              maxWidth: "375px",
              height: "80px",
              display: "flex",
              gap: "32px",
            }}
          >
            <Form.Label className="simulacao-form-title">
              Local de Instalação - É usina local?
            </Form.Label>
            <FormSwitch
              type="switch"
              name="local"
              label=""
              value={formik.values.local}
              onBlur={formik.handleBlur}
              onChange={(e: any) => resetSimultaneidade(e)}
              checked={formik.values.local}
            />
          </Form.Group>

          <Form.Group
            controlId="descontoTusdApe"
            style={{
              width: "100%",
              maxWidth: "375px",
              height: "80px",
              display: "flex",
              gap: "32px",
            }}
          >
            <Form.Label className="simulacao-form-title">
              Possui desconto de TUSD APE?
            </Form.Label>
            <FormSwitch
              type="switch"
              name="descontoTusdApe"
              label=""
              value={formik.values.descontoTusdApe}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              checked={formik.values.descontoTusdApe}
            />
          </Form.Group>

          {!ocultaCampo(formik.values.situacaoUsina, "simultaneidade") && (
            <Form.Group
              controlId="simulationCNPJ"
              style={{
                width: "100%",
                maxWidth: "375px",
                height: "80px",
              }}
            >
              <Form.Label className="simulacao-form-title">
                Fator de Simultaneidade <span>*</span>
              </Form.Label>
              <InputGroup className="mb-3">
                <Form.Control
                  name="simultaneidade"
                  placeholder="Porcentagem"
                  onBlur={formik.handleBlur}
                  value={formik.values.simultaneidade}
                  onChange={formik.handleChange}
                  isInvalid={
                    !!formik.touched.simultaneidade &&
                    !!formik.errors.simultaneidade
                  }
                  min={0}
                  max={100}
                  required={buttonSelected !== "ML"}
                  disabled={!formik.values.local}
                />
                <InputGroup.Text>%</InputGroup.Text>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.simultaneidade}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          )}

          {!ocultaCampo(formik.values.situacaoUsina, "geracao") && (
            <SimulatorTextField
              formik={formik}
              name="geracao"
              controlId="simulationCNPJ"
              label={
                <>
                  {labelGeracao(formik.values.situacaoUsina)} <span>*</span>
                </>
              }
              placeholder="Em kWh"
              unit="kWh"
              required={buttonSelected !== "ML"}
            />
          )}

          {!ocultaCampo(formik.values.situacaoUsina, "demandaUsina") && (
            <SimulatorTextField
              formik={formik}
              name="demandaUsina"
              controlId="simulationCNPJ"
              label={
                <>
                  {labelDemandaUsina(formik.values.situacaoUsina)}{" "}
                  <span>*</span>
                </>
              }
              placeholder="Em kW"
              unit="kW"
              required={buttonSelected !== "ML"}
            />
          )}

          {!ocultaCampo(formik.values.situacaoUsina, "potenciaUsina") && (
            <SimulatorTextField
              formik={formik}
              name="potenciaUsina"
              controlId="simulationCNPJ"
              label={
                <>
                  Potência da Usina <span>*</span>
                </>
              }
              placeholder="Em kWp"
              unit="kWp"
              required={buttonSelected !== "ML"}
            />
          )}

          {!ocultaCampo(formik.values.situacaoUsina, "custoProjeto") && (
            <SimulatorTextField
              formik={formik}
              name="custoProjeto"
              controlId="simulationCNPJ"
              label={
                <>
                  Investimento <span>*</span>
                </>
              }
              placeholder="Em reais"
              prefix="R$"
              required={buttonSelected !== "ML"}
            />
          )}
        </>
      )}
    </div>

    {proposalPermission && buttonSelected !== "APEBESS" && (
      <>
        <div>
          <span className="simulacao-form-header">
            Dados para gerar proposta de UFV
          </span>
          <hr />
        </div>

        <div className="formLine">
          <Form.Group
            controlId="generateProposal"
            style={{
              width: "100%",
              maxWidth: "375px",
              height: "80px",
              display: "flex",
              gap: "32px",
            }}
          >
            <Form.Label className="simulacao-form-title">
              Gerar proposta?
            </Form.Label>
            <FormSwitch
              type="switch"
              name="generateProposal"
              label=""
              value={formik.values.generateProposal}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              checked={formik.values.generateProposal}
            />
          </Form.Group>

          {formik.values.generateProposal && (
            <>
              <Form.Group
                controlId="lcoe"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  LCOE <span>*</span>
                </Form.Label>
                <InputGroup className="mb-3">
                  <InputGroup.Text>R$/MWh</InputGroup.Text>
                  <Form.Control
                    name="lcoe"
                    placeholder="Em R$/MWh"
                    onBlur={formik.handleBlur}
                    value={formik.values.lcoe}
                    onChange={formik.handleChange}
                    isInvalid={!!formik.touched.lcoe && !!formik.errors.lcoe}
                    min={0}
                    max={100}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.lcoe}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              <Form.Group
                controlId="clientLogo"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                  display: "flex",
                  gap: "32px",
                }}
              >
                <Form.File
                  type="file"
                  name="clientLogo"
                  accept="image/*"
                  label="Upload logo do cliente"
                  onBlur={formik.handleBlur}
                  onChange={(e: any) => handleFileChange(e, "clientLogo")}
                />
              </Form.Group>
            </>
          )}
        </div>

        {formik.values.generateProposal && (
          <>
            <div className="formLine">
              <Form.Group
                controlId="partnerLogo"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                  display: "flex",
                  gap: "32px",
                }}
              >
                <Form.File
                  type="file"
                  name="partnerLogo"
                  accept="image/*"
                  label="Upload logo do parceiro"
                  onBlur={formik.handleBlur}
                  onChange={(e: any) => handleFileChange(e, "partnerLogo")}
                />
              </Form.Group>

              <Form.Group
                controlId="simulationLocal"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                  display: "flex",
                  gap: "32px",
                }}
              >
                <Form.File
                  type="file"
                  name="projectImage1"
                  accept="image/*"
                  label="Upload imagem da disposição dos módulos"
                  onBlur={formik.handleBlur}
                  onChange={(e: any) => handleFileChange(e, "projectImage1")}
                />
              </Form.Group>

              <Form.Group
                controlId="simulationLocal"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                  display: "flex",
                  gap: "32px",
                }}
              >
                <Form.File
                  type="file"
                  name="projectImage2"
                  accept="image/*"
                  label="Upload imagem do mapa de localização"
                  onBlur={formik.handleBlur}
                  onChange={(e: any) => handleFileChange(e, "projectImage2")}
                />
              </Form.Group>
            </div>

            <div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span className="simulacao-form-header">
                  Dados técnicos do projeto
                </span>
                <span style={{ color: "#7e7e82" }}>
                  Lembre de pular linhas para formatar melhor o PDF
                </span>
                <hr />
              </div>
            </div>

            <div className="formLine">
              <Form.Group
                controlId="localization"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  minHeight: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Localização <span>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="localization"
                  placeholder="Digite a(s) localização(ões) do projeto"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.localization}
                  isInvalid={
                    !!formik.touched.localization &&
                    !!formik.errors.localization
                  }
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.localization}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group
                controlId="modules"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  minHeight: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Módulos <span>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="modules"
                  placeholder="Digite o(s) módulo(s) do projeto"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.modules}
                  isInvalid={
                    !!formik.touched.modules && !!formik.errors.modules
                  }
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.modules}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group
                controlId="inverter"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  minHeight: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Inversores <span>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="inverter"
                  placeholder="Digite o(s) inversor(es) do projeto"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.inverter}
                  isInvalid={
                    !!formik.touched.inverter && !!formik.errors.inverter
                  }
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inverter}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group
                controlId="structure"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  minHeight: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Estrutura da montagem <span>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="structure"
                  placeholder="Digite a(s) estrutura(s) de montagem do projeto"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.structure}
                  isInvalid={
                    !!formik.touched.structure && !!formik.errors.structure
                  }
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.structure}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div>
              <span className="simulacao-form-header">Tipos de instalação</span>
              <hr />
            </div>

            {formik.values.items.length > 0 ? (
              formik.values.items.map((item, index: number) => (
                <div key={index}>
                  <div className="formLine">
                    <Form.Group
                      controlId="nome"
                      style={{
                        width: "100%",
                        maxWidth: "375px",
                        height: "80px",
                      }}
                    >
                      <Form.Label className="simulacao-form-title">
                        Nome da instalação <span>*</span>
                      </Form.Label>
                      <Form.Control
                        name="nome"
                        placeholder="Nome da instalação"
                        onBlur={() =>
                          formik.setFieldTouched(`items[${index}].nome`)
                        }
                        value={item.nome}
                        onChange={(event) => handleItemChange(index, event)}
                        isInvalid={
                          !!(formik.touched.items?.[index] as any)?.nome &&
                          !!(formik.errors.items?.[index] as any)?.nome
                        }
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {(formik.errors.items?.[index] as any)?.nome}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group
                      controlId="potencia"
                      style={{
                        width: "100%",
                        maxWidth: "375px",
                        height: "80px",
                      }}
                    >
                      <Form.Label className="simulacao-form-title">
                        Potência da instalação <span>*</span>
                      </Form.Label>
                      <InputGroup className="mb-3">
                        <Form.Control
                          name="potencia"
                          placeholder="Em MWp"
                          onBlur={() =>
                            formik.setFieldTouched(`items[${index}].potencia`)
                          }
                          value={item.potencia}
                          onChange={(event) => handleItemChange(index, event)}
                          isInvalid={
                            !!(formik.touched.items?.[index] as any)
                              ?.potencia &&
                            !!(formik.errors.items?.[index] as any)?.potencia
                          }
                          required
                        />
                        <InputGroup.Text>MWp</InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {(formik.errors.items?.[index] as any)?.potencia}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group
                      controlId="geradorFotovoltaico"
                      style={{
                        width: "100%",
                        maxWidth: "375px",
                        height: "80px",
                      }}
                    >
                      <Form.Label className="simulacao-form-title">
                        Gerador fotovoltaico <span>*</span>
                      </Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Text>R$/Wp</InputGroup.Text>
                        <Form.Control
                          name="geradorFotovoltaico"
                          placeholder="Em R$/Wp"
                          onBlur={() =>
                            formik.setFieldTouched(
                              `items[${index}].geradorFotovoltaico`
                            )
                          }
                          value={item.geradorFotovoltaico}
                          onChange={(event) => handleItemChange(index, event)}
                          isInvalid={
                            !!(formik.touched.items?.[index] as any)
                              ?.geradorFotovoltaico &&
                            !!(formik.errors.items?.[index] as any)
                              ?.geradorFotovoltaico
                          }
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {
                            (formik.errors.items?.[index] as any)
                              ?.geradorFotovoltaico
                          }
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group
                      controlId="projetoHomologacao"
                      style={{
                        width: "100%",
                        maxWidth: "375px",
                        height: "80px",
                      }}
                    >
                      <Form.Label className="simulacao-form-title">
                        Projeto e homologação <span>*</span>
                      </Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Text>R$/Wp</InputGroup.Text>
                        <Form.Control
                          name="projetoHomologacao"
                          placeholder="Em R$/Wp"
                          onBlur={() =>
                            formik.setFieldTouched(
                              `items[${index}].projetoHomologacao`
                            )
                          }
                          value={item.projetoHomologacao}
                          onChange={(event) => handleItemChange(index, event)}
                          isInvalid={
                            !!(formik.touched.items?.[index] as any)
                              ?.projetoHomologacao &&
                            !!(formik.errors.items?.[index] as any)
                              ?.projetoHomologacao
                          }
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {
                            (formik.errors.items?.[index] as any)
                              ?.projetoHomologacao
                          }
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group
                      controlId="epc"
                      style={{
                        width: "100%",
                        maxWidth: "375px",
                        height: "80px",
                      }}
                    >
                      <Form.Label className="simulacao-form-title">
                        EPC <span>*</span>
                      </Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Text>R$/Wp</InputGroup.Text>
                        <Form.Control
                          name="epc"
                          placeholder="Em R$/Wp"
                          onBlur={() =>
                            formik.setFieldTouched(`items[${index}].epc`)
                          }
                          value={item.epc}
                          onChange={(event) => handleItemChange(index, event)}
                          isInvalid={
                            !!(formik.touched.items?.[index] as any)?.epc &&
                            !!(formik.errors.items?.[index] as any)?.epc
                          }
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {(formik.errors.items?.[index] as any)?.epc}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="danger"
                        onClick={() => removeItem(index)}
                        style={{
                          marginLeft: "10px",
                          marginTop: "10px",
                        }}
                      >
                        Remover
                      </Button>
                    </div>
                  </div>

                  <hr />
                </div>
              ))
            ) : (
              <div style={{ color: "red" }}>Nenhuma instalação adicionada</div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="primary"
                onClick={addItem}
                style={{ margin: "10px 0" }}
              >
                Adicionar Item
              </Button>
            </div>
          </>
        )}
      </>
    )}
  </>
);
